import React from 'react';
import FlipMove from 'react-flip-move';

import UploadIcon from './UploadIcon.svg';
import './MultiFileUploader.css';

const styles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  width: '100%'
};

const ERROR = {
  NOT_SUPPORTED_EXTENSION: 'NOT_SUPPORTED_EXTENSION',
  FILESIZE_TOO_LARGE: 'FILESIZE_TOO_LARGE'
}

const IMAGE_FILE_EXTENSION = /\.(:?png|gif|bmp|jpg|jpeg)(:?\?*)?$/i

interface IMultiFileUploaderProps {
  style: React.CSSProperties,
  fileContainerStyle: React.CSSProperties,
  className: string,
  onChange: (files: File[], filenames: string[]) => void
  buttonClassName: string,
  buttonStyles: React.CSSProperties,
  buttonType: 'button' | 'submit' | 'reset',
  withPreview: boolean,
  accept: string,
  name: string,
  withIcon: boolean,
  buttonText: string,
  withLabel: boolean,
  label: string,
  labelStyles: React.CSSProperties,
  labelClass: string,
  fileExtensions: string[],
  maxFileSize: number,
  fileSizeError: string,
  fileTypeError: string,
  errorClass: string,
  errorStyle: React.CSSProperties,
  singleFile: boolean,
  filenames: string[],
}

interface IMultiFileUploaderState {
  filenames: string[]
  files: File[]
  fileErrors: { type: string, name: string  }[]
}

class MultiFileUploaderComponent extends React.Component<IMultiFileUploaderProps, IMultiFileUploaderState> {
  static defaultProps = {
    className: '',
    fileContainerStyle: {},
    buttonClassName: '',
    buttonStyles: {},
    withPreview: false,
    accept: 'image/*',
    name: '',
    withIcon: true,
    buttonText: 'Choose images',
    buttonType: 'button',
    withLabel: true,
    label: 'Max file size: 5mb, accepted: jpg|gif|png',
    labelStyles: {},
    labelClass: '',
    fileExtension: ['.jpg', '.jpeg', '.gif', '.png'],
    maxFileSize: 5242880,
    fileSizeError: ' file size is too big',
    fileTypeError: ' is not a supported file extension',
    errorClass: '',
    style: {},
    errorStyle: {},
    singleFile: false,
    onChange: () => {},
    defaultFiles: []
  }

  private inputElement: HTMLInputElement | null = null;

  public state: IMultiFileUploaderState = {
    filenames: [...this.props.filenames ?? []],
    files: [],
    fileErrors: []
  }

  public componentDidUpdate(prevProps: IMultiFileUploaderProps, prevState: IMultiFileUploaderState) {
    if(prevProps.filenames !== this.props.filenames){
      this.setState({filenames: prevProps.filenames}, () => {
        if(prevState.files !== this.state.files){
          this.props.onChange(this.state.files, this.state.filenames);
        }
      });
    } else {
      if(prevState.files !== this.state.files){
        this.props.onChange(this.state.files, this.state.filenames);
      }
    }
  }

  private hasExtension = (fileName: string) => {
    const pattern = '(' + this.props.fileExtensions.join('|').replace(/\./g, '\\.') + ')$';
    return new RegExp(pattern, 'i').test(fileName);
  }

  private onDropFile = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const files = event.currentTarget.files ?? [];
    const allFilePromises = [];
    const fileErrors: { type: string, name: string }[] = [];

    for (let i = 0; i < files.length; i++) {
      let file = files[i];
      let fileError = {
        name: file.name,
        type: '',
      };

      if (!this.hasExtension(file.name)) {
        fileError = {
          ...fileError,
          type: ERROR.NOT_SUPPORTED_EXTENSION,
        }
        fileErrors.push(fileError);
        continue;
      }

      if(file.size > this.props.maxFileSize) {
        fileError = {
          ...fileError,
          type: ERROR.FILESIZE_TOO_LARGE
        }
        fileErrors.push(fileError);
        continue;
      }

      allFilePromises.push(this.readFile(file));
    }

    this.setState({
      fileErrors
    });

    const { singleFile } = this.props;

    Promise.all(allFilePromises).then((newFilesData) => {
      const dataURLs = singleFile?[]:this.state.filenames.slice();
      const files = singleFile?[]:this.state.files.slice();

      newFilesData.forEach(newFileData => {
        dataURLs.push(newFileData.dataURL);
        files.push(newFileData.file);
      });

      this.setState({filenames: dataURLs, files: files});
    });
  }

  private onUploadClick = (event: any) => {
    event.target.value = null;
  }

  private readFile = (file: File) =>
    new Promise<{ file: File, dataURL: string }>((resolve) => {
      const reader = new FileReader()

      reader.onload = (event) => {
        let dataURL = event.target!.result as string
        dataURL = dataURL.replace(';base64', `;name=${file.name};base64`)
        resolve({ file, dataURL })
      }

      reader.readAsDataURL(file)
    })

  private removeFile(filename: string) {
    const removeIndex = this.state.filenames.findIndex(e => e === filename);
    const filteredFilenames = this.state.filenames.filter((_, index) => index !== removeIndex);
    const filteredFiles = this.state.files.filter((_, index) => index !== removeIndex);

    this.setState({filenames: filteredFilenames, files: filteredFiles}, () => {
      this.props.onChange(this.state.files, this.state.filenames);
    });
  }

  private renderErrors = () => {
    const { fileErrors } = this.state;
    return fileErrors.map((fileError, index) => {
      return (
        <div className={'errorMessage ' + this.props.errorClass} key={index} style={this.props.errorStyle}>
          * {fileError.name} {fileError.type === ERROR.FILESIZE_TOO_LARGE ? this.props.fileSizeError: this.props.fileTypeError}
        </div>
      );
    });
  }

  private renderIcon = () =>
    this.props.withIcon
      ? <img src={UploadIcon} className='uploadIcon'	alt='Upload Icon' />
      : null

  private renderLabel = () =>
    this.props.withLabel
      ? <p className={this.props.labelClass} style={this.props.labelStyles}>{this.props.label}</p>
      : null

  private renderPreview = () =>
    <div className='uploadPicturesWrapper'>
      <FlipMove enterAnimation='fade' leaveAnimation='fade' style={styles}>
        { this.renderPreviewPictures() }
      </FlipMove>
    </div>

  private isImage = (filename: string) =>
    IMAGE_FILE_EXTENSION.test(this.sanitizeBlobName(filename))

  private sanitizeBlobName = (blobName: string) =>
    blobName.split(';name=')[1].split(';')[0]

  private renderPreviewPictures = () => {
    return this.state.filenames.map((filename, index) => {
      return (
        <div key={index} className='uploadPictureContainer'>
          <div className='deleteImage' onClick={() => this.removeFile(filename)}>X</div>
          { /* FIXME: preview .PDF or whatnot for non-image files */ }
          { this.isImage(filename)
            ? <img src={filename} className='uploadPicture' alt='preview'/>
            : <div className='uploadPicture' style={{wordBreak: "break-all"}}>{this.sanitizeBlobName(filename)}</div>}
        </div>
      );
    });
  }

  private triggerFileUpload = () => {
    this.inputElement?.click();
  }

  private onInputRef = (input: HTMLInputElement | null) => {
    this.inputElement = input
  }

  public render() {
    return (
      <div className={'fileUploader ' + this.props.className} style={this.props.style}>
        <div className='fileContainer' style={this.props.fileContainerStyle}>
          {this.renderIcon()}
          {this.renderLabel()}
          <div className='errorsContainer'>
            {this.renderErrors()}
          </div>
          <button
            type={this.props.buttonType}
            className={'chooseFileButton ' + this.props.buttonClassName}
            style={this.props.buttonStyles}
            onClick={this.triggerFileUpload}
          >
            {this.props.buttonText}
          </button>
          <input
            type='file'
            ref={this.onInputRef}
            name={this.props.name}
            multiple={!this.props.singleFile}
            onChange={this.onDropFile}
            onClick={this.onUploadClick}
            accept={this.props.accept}
          />
          { this.props.withPreview ? this.renderPreview() : null }
        </div>
      </div>
    )
  }
}

export const MultiFileUploader = MultiFileUploaderComponent as React.ComponentClass<Partial<IMultiFileUploaderProps>>

